import {
  formatDuration,
  type DurationUnit,
  type HistoryChange,
  type HistoryChangeKind,
  type HistoryEntityType,
  type HistoryEntry,
  type HistoryValue,
} from '@forecastify/shared';
import type { Locale, MessageKey, Translate } from '../../i18n';
import { htmlToText } from './history-html';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ ux-pass-2 · the words of a history row. THE SERVER RESOLVES, THE SCREEN    │
 * │ LABELS (`packages/shared/src/history/audit.ts`).                           │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Every id on the wire is already a name; what is left for the client is the vocabulary — which
 * field, which verb, which kind of record — and how a value of each `kind` is formatted for the
 * reader's locale. One module, so the audit page and every entity's tab say *Státusz* for the same
 * key rather than one of them saying *status_id*.
 *
 * ⚠️ **Typed maps of literal keys** (`Record<…, MessageKey>`), the shape `integration-bits.tsx`
 * uses: a missing translation is a type error, and an unknown key from the server has a readable
 * fallback instead of a blank — an audit row nobody can read is still evidence that something
 * happened.
 */

/** ⚠️ Exhaustive over `HISTORY_ENTITY_TYPES` — a type added to the contract fails the typecheck. */
const ENTITY_LABEL: Readonly<Record<HistoryEntityType, MessageKey>> = {
  task: 'history.entity.task',
  comment: 'history.entity.comment',
  worklog: 'history.entity.worklog',
  project: 'history.entity.project',
  project_member: 'history.entity.project_member',
  allocation: 'history.entity.allocation',
  client: 'history.entity.client',
  contract: 'history.entity.contract',
  line_item: 'history.entity.line_item',
  period: 'history.entity.period',
  invoice: 'history.entity.invoice',
  external_cost: 'history.entity.external_cost',
  person: 'history.entity.person',
  leave_request: 'history.entity.leave_request',
  overtime_request: 'history.entity.overtime_request',
  file: 'history.entity.file',
  report: 'history.entity.report',
  metafield: 'history.entity.metafield',
  role: 'history.entity.role',
  integration: 'history.entity.integration',
  settings: 'history.entity.settings',
  workspace: 'history.entity.workspace',
  deal: 'history.entity.deal',
  contact: 'history.entity.contact',
  other: 'history.entity.other',
};

const KIND_LABEL: Readonly<Record<HistoryChangeKind, MessageKey>> = {
  create: 'history.kind.create',
  update: 'history.kind.update',
  delete: 'history.kind.delete',
  other: 'history.kind.other',
};

export type HistoryChannel = NonNullable<HistoryEntry['channel']>;

const CHANNEL_LABEL: Readonly<Record<HistoryChannel, MessageKey>> = {
  ui: 'history.channel.ui',
  api: 'history.channel.api',
  agent: 'history.channel.agent',
  mcp: 'history.channel.mcp',
};

/**
 * The VERB of an audit action — what follows its first dot (`task.comment_add` → `comment_add`).
 *
 * ⚠️ **Only the verbs that say more than their kind.** `create`, `update` and `delete` are the
 * badge beside them already; writing *Módosítás · módosítás* would be the same word twice.
 */
const VERB_LABEL: Readonly<Record<string, MessageKey>> = {
  reorder: 'history.action.reorder',
  revoke: 'history.action.revoke',
  export: 'history.action.export',
  import: 'history.action.import',
  restore: 'history.action.restore',
  archive: 'history.action.archive',
  attach: 'history.action.attach',
  detach: 'history.action.detach',
  sync: 'history.action.sync',
  link: 'history.action.link',
  unlink: 'history.action.unlink',
  decide: 'history.action.decide',
  request: 'history.action.request',
  comment: 'history.action.comment',
  comment_add: 'history.action.comment',
  comment_edit: 'history.action.commentEdit',
  comment_delete: 'history.action.commentDelete',
  assign: 'history.action.assign',
  unassign: 'history.action.unassign',
  member_add: 'history.action.memberAdd',
  member_remove: 'history.action.memberRemove',
  member_update: 'history.action.memberUpdate',
  duplicate: 'history.action.duplicate',
  set_default: 'history.action.setDefault',
  default: 'history.action.setDefault',
  activate: 'history.action.activate',
  deactivate: 'history.action.deactivate',
  invite: 'history.action.invite',
  invite_resend: 'history.action.invite',
  invite_revoke: 'history.action.revoke',
  set_values: 'history.action.setValues',
  depend: 'history.action.depend',
  undepend: 'history.action.undepend',
  raise: 'history.action.raise',
  dismiss: 'history.action.dismiss',
  install: 'history.action.install',
  uninstall: 'history.action.uninstall',
  pause: 'history.action.pause',
  resume: 'history.action.resume',
  roles_set: 'history.action.rolesSet',
  onboarded: 'history.action.onboarded',
};

/**
 * The fields worth a word. `field` arrives in camelCase; `fieldLabel` also tries it without an
 * `Id` / `MemberId` tail, so `statusId`, `assigneeMemberId` and `pmMemberId` land on *Státusz*,
 * *Felelős* and *PM* without three more entries each.
 */
const FIELD_LABEL: Readonly<Record<string, MessageKey>> = {
  title: 'history.field.title',
  name: 'history.field.name',
  displayName: 'history.field.name',
  givenName: 'history.field.givenName',
  familyName: 'history.field.familyName',
  email: 'history.field.email',
  phone: 'history.field.phone',
  status: 'history.field.status',
  state: 'history.field.status',
  priority: 'history.field.priority',
  assignee: 'history.field.assignee',
  project: 'history.field.project',
  client: 'history.field.client',
  contract: 'history.field.contract',
  estimate: 'history.field.estimate',
  estimateHours: 'history.field.estimate',
  startsOn: 'history.field.startsOn',
  endsOn: 'history.field.endsOn',
  dueOn: 'history.field.dueOn',
  lineItem: 'history.field.lineItem',
  parentTask: 'history.field.parentTask',
  description: 'history.field.description',
  amount: 'history.field.amount',
  currency: 'history.field.currency',
  currencyCode: 'history.field.currency',
  budget: 'history.field.budget',
  revenue: 'history.field.revenue',
  price: 'history.field.price',
  unitPrice: 'history.field.unitPrice',
  quantity: 'history.field.quantity',
  unit: 'history.field.unit',
  type: 'history.field.type',
  owner: 'history.field.owner',
  pm: 'history.field.pm',
  role: 'history.field.role',
  roles: 'history.field.role',
  projectRole: 'history.field.role',
  defaultProjectRole: 'history.field.defaultProjectRole',
  team: 'history.field.team',
  manager: 'history.field.manager',
  tracksTime: 'history.field.tracksTime',
  leaveType: 'history.field.leaveType',
  overtimeType: 'history.field.overtimeType',
  from: 'history.field.from',
  to: 'history.field.to',
  hours: 'history.field.hours',
  workedOn: 'history.field.workedOn',
  note: 'history.field.note',
  body: 'history.field.body',
  bodyHtml: 'history.field.body',
  tags: 'history.field.tags',
  metafields: 'history.field.metafields',
  color: 'history.field.color',
  code: 'history.field.code',
  recurrence: 'history.field.recurrence',
  assignment: 'history.field.assignment',
  active: 'history.field.active',
  issuedOn: 'history.field.issuedOn',
  paidOn: 'history.field.paidOn',
  incurredOn: 'history.field.incurredOn',
  number: 'history.field.number',
  employmentType: 'history.field.employmentType',
  weeklyHours: 'history.field.weeklyHours',
  settlement: 'history.field.settlement',
  multiplier: 'history.field.multiplier',
  isDefault: 'history.field.isDefault',
  position: 'history.field.position',
  rate: 'history.field.rate',
  billable: 'history.field.billable',
  member: 'history.field.member',
  invoice: 'history.field.invoice',
  period: 'history.field.period',
};

export function entityTypeLabel(t: Translate, type: HistoryEntityType): string {
  return t(ENTITY_LABEL[type]);
}

export function kindLabel(t: Translate, kind: HistoryChangeKind): string {
  return t(KIND_LABEL[kind]);
}

export function channelLabel(t: Translate, channel: HistoryEntry['channel']): string {
  return channel === null ? '–' : t(CHANNEL_LABEL[channel]);
}

/**
 * The verb beside the kind badge, or `null` when the badge already says it — `task.update` is a
 * *Módosítás* and nothing more.
 */
export function verbLabel(t: Translate, action: string): string | null {
  const verb = action.slice(action.indexOf('.') + 1);
  const key = VERB_LABEL[verb];
  return key === undefined ? null : t(key);
}

/** The words the row's action stands for: the verb when it has one, otherwise its kind. */
export function actionLabel(t: Translate, entry: Pick<HistoryEntry, 'action' | 'kind'>): string {
  return verbLabel(t, entry.action) ?? kindLabel(t, entry.kind);
}

/**
 * ⚠️ **The readable fallback**: `unitPrice` becomes *Unit price*. Not translated, and it cannot
 * be — but it is a label somebody can read and report, where the raw key reads as a fault.
 */
function deCamel(field: string): string {
  const words = field
    .replace(/([a-z0-9])([A-Z])/gu, '$1 $2')
    .replace(/[_-]+/gu, ' ')
    .toLowerCase()
    .trim();
  return words === '' ? field : words.charAt(0).toUpperCase() + words.slice(1);
}

export function fieldLabel(t: Translate, field: string): string {
  const candidates = [field, field.replace(/MemberIds?$/u, ''), field.replace(/Ids?$/u, '')];
  for (const candidate of candidates) {
    const key = FIELD_LABEL[candidate];
    if (key !== undefined) return t(key);
  }
  return deCamel(field.replace(/(?:MemberIds?|Ids?)$/u, '') || field);
}

/** `hu` and `en` are both tags `Intl` knows; the reader's locale decides the order of a date. */
const DATE: Intl.DateTimeFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
const DATE_TIME: Intl.DateTimeFormatOptions = { ...DATE, hour: '2-digit', minute: '2-digit' };

/** An instant, in the reader's locale and timezone. Unparseable input renders as itself. */
export function formatInstant(iso: string, locale: Locale): string {
  const at = new Date(iso);
  return Number.isFinite(at.getTime()) ? at.toLocaleString(locale, DATE_TIME) : iso;
}

/**
 * A calendar date, which is NOT an instant (`hard-won.md`): built from its own parts in local
 * time, so `2026-10-14` never becomes the 13th west of Greenwich.
 */
export function formatCalendarDate(text: string, locale: Locale): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/u.exec(text);
  if (match === null) return text;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return date.toLocaleDateString(locale, DATE);
}

function formatMoneyText(text: string, locale: Locale): string {
  const match = /^(-?\d+(?:\.\d+)?)(?:\s+(\S.*))?$/u.exec(text.trim());
  if (match === null) return text;
  const figure = Number(match[1]).toLocaleString(locale, { maximumFractionDigits: 2 });
  return match[2] === undefined ? figure : `${figure} ${match[2]}`;
}

/**
 * One side of a change as plain text, formatted by its `kind`.
 *
 * ⚠️ `html` comes back as its words here — the summary column is one line. The detail view
 * renders it as rich text instead (`HistoryValueView`).
 */
/**
 * ⚠️ 3 Nov 2026 · `unit` is the reader's hours or man-days (`decisions.md` DU-1) — `useDuration()`
 * at the component; hours when a caller has none to pass.
 */
export function valueText(
  t: Translate,
  locale: Locale,
  value: HistoryValue | null,
  unit: DurationUnit = 'hours',
): string {
  if (value === null || value.text === '') return '–';
  switch (value.kind) {
    case 'date':
      return formatCalendarDate(value.text, locale);
    case 'datetime':
      return formatInstant(value.text, locale);
    case 'hours':
      return Number.isFinite(Number(value.text))
        ? formatDuration(value.text, unit, locale)
        : value.text;
    case 'money':
      return formatMoneyText(value.text, locale);
    case 'bool':
      return value.text === 'true'
        ? t('common.yes')
        : value.text === 'false'
          ? t('common.no')
          : value.text;
    case 'html':
      return htmlToText(value.text) || '–';
    case 'text':
    case 'list':
      return value.text;
  }
}

/** `Státusz: Backlog → Folyamatban`. A creation has no before, a deletion no after. */
export function changeText(
  t: Translate,
  locale: Locale,
  change: HistoryChange,
  unit: DurationUnit = 'hours',
): string {
  const label = fieldLabel(t, change.field);
  const side = (value: HistoryValue | null): string => valueText(t, locale, value, unit);
  if (change.before === null) return `${label}: ${side(change.after)}`;
  if (change.after === null) return `${label}: ${side(change.before)}`;
  return `${label}: ${side(change.before)} → ${side(change.after)}`;
}

const EXCERPT = 90;

/**
 * The Változás column, in one line: the first change and how many more, or the note's opening
 * words for a row that changed no field (a comment, an approval).
 */
export function summaryText(
  t: Translate,
  locale: Locale,
  entry: HistoryEntry,
  unit: DurationUnit = 'hours',
): string {
  const [first, ...rest] = entry.changes;
  if (first !== undefined) {
    const head = changeText(t, locale, first, unit);
    return rest.length === 0
      ? head
      : `${head} · ${t('history.summary.more', { count: rest.length })}`;
  }
  if (entry.note !== null) {
    const words = valueText(t, locale, entry.note);
    return words.length > EXCERPT ? `${words.slice(0, EXCERPT).trimEnd()}…` : words;
  }
  return '';
}

/** What the row is about: *Feladat · HSO-142 · Story clustering*. */
export function entityText(t: Translate, entry: HistoryEntry): string {
  return [entityTypeLabel(t, entry.entity.type), entry.entity.identifier, entry.entity.label]
    .filter((part): part is string => part !== null && part !== '')
    .join(' · ');
}
